/**
 * 💡 HelpCenter - Hilfe-Hub für Tutorial, FAQ, What's New
 */
import React, { useState, useEffect } from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import { makeTr } from '../../services/trLookup';
import { isSetupEnabled } from '../../services/accountProfile';
import { submitUserFeedback } from '../../services/errorReport';
import BatchTutorialVideo from '../BatchTutorialVideo';
import './HelpCenter.css';

// ============================================
// 📚 HELP DATA
// ============================================

// Tutorial-Kategorien mit eigenen Slides
const tutorialCategories = [
  {
    id: 'basics',
    icon: '📱',
    titleKey: 'help.tutorials.basics.title',
    descKey: 'help.tutorials.basics.desc',
    steps: [
      { id: 1, icon: '👋', titleKey: 'help.tutorial.step1.title', textKey: 'help.tutorial.step1.text' },
      { id: 2, icon: '📦', titleKey: 'help.tutorial.step2.title', textKey: 'help.tutorial.step2.text' },
      { id: 3, icon: '📷', titleKey: 'help.tutorial.step3.title', textKey: 'help.tutorial.step3.text' },
      { id: 4, icon: '✏️', titleKey: 'help.tutorial.step4.title', textKey: 'help.tutorial.step4.text' },
      { id: 5, icon: '📝', titleKey: 'help.tutorial.step5.title', textKey: 'help.tutorial.step5.text' },
      { id: 6, icon: '🗄️', titleKey: 'help.tutorial.step6.title', textKey: 'help.tutorial.step6.text' },
      { id: 7, icon: '🔍', titleKey: 'help.tutorial.step7.title', textKey: 'help.tutorial.step7.text' },
      { id: 8, icon: '✅', titleKey: 'help.tutorial.step8.title', textKey: 'help.tutorial.step8.text' },
      { id: 9, icon: '🎉', titleKey: 'help.tutorial.step9.title', textKey: 'help.tutorial.step9.text' },
    ]
  },
  {
    id: 'packages',
    icon: '🔄',
    titleKey: 'help.tutorials.packages.title',
    descKey: 'help.tutorials.packages.desc',
    steps: [
      { id: 1, icon: '✏️', titleKey: 'help.tutorials.packages.edit.title', textKey: 'help.tutorials.packages.edit.text' },
      { id: 2, icon: '↩️', titleKey: 'help.tutorials.packages.return.title', textKey: 'help.tutorials.packages.return.text' },
      { id: 3, icon: '🗑️', titleKey: 'help.tutorials.packages.delete.title', textKey: 'help.tutorials.packages.delete.text' },
      { id: 4, icon: '🧹', titleKey: 'help.tutorials.packages.cleanup.title', textKey: 'help.tutorials.packages.cleanup.text' },
    ]
  },
  {
    id: 'cloud',
    icon: '☁️',
    titleKey: 'help.tutorials.cloud.title',
    descKey: 'help.tutorials.cloud.desc',
    steps: [
      { id: 1, icon: '🔐', titleKey: 'help.tutorials.cloud.login.title', textKey: 'help.tutorials.cloud.login.text' },
      { id: 2, icon: '🔄', titleKey: 'help.tutorials.cloud.sync.title', textKey: 'help.tutorials.cloud.sync.text' },
      { id: 3, icon: '📱', titleKey: 'help.tutorials.cloud.multidevice.title', textKey: 'help.tutorials.cloud.multidevice.text' },
      { id: 4, icon: '💾', titleKey: 'help.tutorials.cloud.backup.title', textKey: 'help.tutorials.cloud.backup.text' },
      { id: 5, icon: '⚠️', titleKey: 'help.tutorials.cloud.serverfirst.title', textKey: 'help.tutorials.cloud.serverfirst.text' },
      { id: 6, icon: '💡', titleKey: 'help.tutorials.cloud.serverfirst2.title', textKey: 'help.tutorials.cloud.serverfirst2.text' },
    ]
  },
  {
    id: 'tablet',
    icon: '📺',
    titleKey: 'help.tutorials.tablet.title',
    descKey: 'help.tutorials.tablet.desc',
    steps: [
      { id: 1, icon: '🔍', titleKey: 'help.tutorials.tablet.search.title', textKey: 'help.tutorials.tablet.search.text' },
      { id: 2, icon: '📱', titleKey: 'help.tutorials.tablet.kiosk.title', textKey: 'help.tutorials.tablet.kiosk.text' },
      { id: 3, icon: '�', titleKey: 'help.tutorials.tablet.notify.title', textKey: 'help.tutorials.tablet.notify.text' },
    ]
  },
  {
    id: 'settings',
    icon: '⚙️',
    titleKey: 'help.tutorials.settings.title',
    descKey: 'help.tutorials.settings.desc',
    steps: [
      { id: 1, icon: '🌙', titleKey: 'help.tutorials.settings.darkmode.title', textKey: 'help.tutorials.settings.darkmode.text' },
      { id: 2, icon: '🌍', titleKey: 'help.tutorials.settings.language.title', textKey: 'help.tutorials.settings.language.text' },
      { id: 3, icon: '�️', titleKey: 'help.tutorials.settings.maintenance.title', textKey: 'help.tutorials.settings.maintenance.text' },
      { id: 4, icon: '💾', titleKey: 'help.tutorials.settings.backup.title', textKey: 'help.tutorials.settings.backup.text' },
    ]
  },  {
    id: 'premium',
    icon: '⭐',
    titleKey: 'help.tutorials.premium.title',
    descKey: 'help.tutorials.premium.desc',
    steps: [
      { id: 1, icon: '🆓', titleKey: 'help.tutorials.premium.free.title', textKey: 'help.tutorials.premium.free.text' },
      { id: 2, icon: '⭐', titleKey: 'help.tutorials.premium.features.title', textKey: 'help.tutorials.premium.features.text' },
      { id: 3, icon: '💰', titleKey: 'help.tutorials.premium.pricing.title', textKey: 'help.tutorials.premium.pricing.text' },
      { id: 4, icon: '💳', titleKey: 'help.tutorials.premium.payment.title', textKey: 'help.tutorials.premium.payment.text' },
    ]
  },
  // 🧭 Anleitungen zu Setup, Drucker und Regal-QR — mit Bildern aus der App (public/help/<sprache>/).
  // preview: nur sichtbar, solange/sobald das Setup eingeschaltet ist (sonst beschriebe die Hilfe
  // Dinge, die es auf dem Gerät noch nicht gibt).
  {
    id: 'setup', preview: true,
    icon: '🧭',
    titleKey: 'help.guides.setup.title',
    descKey: 'help.guides.setup.desc',
    steps: [
      { id: 1, icon: '🧭', img: 'setup-1.jpg', titleKey: 'help.guides.setup.s1.title', textKey: 'help.guides.setup.s1.text' },
      { id: 2, icon: '❓', img: 'setup-hilfe.jpg', titleKey: 'help.guides.setup.s2.title', textKey: 'help.guides.setup.s2.text' },
      { id: 3, icon: '🔤', img: 'setup-3.jpg', titleKey: 'help.guides.setup.s3.title', textKey: 'help.guides.setup.s3.text' },
      { id: 4, icon: '📷', img: 'setup-5.jpg', titleKey: 'help.guides.setup.s4.title', textKey: 'help.guides.setup.s4.text' },
      { id: 5, icon: '⭐', titleKey: 'help.guides.setup.s5.title', textKey: 'help.guides.setup.s5.text' },
      { id: 6, icon: '📱', img: 'setup-7.jpg', titleKey: 'help.guides.setup.s6.title', textKey: 'help.guides.setup.s6.text' },
    ]
  },
  {
    id: 'printer', preview: true,
    icon: '🖨️',
    titleKey: 'help.guides.printer.title',
    descKey: 'help.guides.printer.desc',
    steps: [
      { id: 0, icon: '🔌', img: 'drucker-0.jpg', titleKey: 'help.guides.printer.s0.title', textKey: 'help.guides.printer.s0.text' },
      { id: 1, icon: '🔵', img: 'drucker-1.jpg', titleKey: 'help.guides.printer.s1.title', textKey: 'help.guides.printer.s1.text' },
      { id: 2, icon: '🖨️', img: 'drucker-2.jpg', titleKey: 'help.guides.printer.s2.title', textKey: 'help.guides.printer.s2.text' },
      { id: 3, icon: '✅', img: 'drucker-3.jpg', titleKey: 'help.guides.printer.s3.title', textKey: 'help.guides.printer.s3.text' },
      { id: 4, icon: '🎨', img: 'drucker-4.jpg', titleKey: 'help.guides.printer.s4.title', textKey: 'help.guides.printer.s4.text' },
      { id: 5, icon: '📋', img: 'drucker-5.jpg', titleKey: 'help.guides.printer.s5.title', textKey: 'help.guides.printer.s5.text' },
      { id: 6, icon: '🛠️', titleKey: 'help.guides.printer.s6.title', textKey: 'help.guides.printer.s6.text' },
    ]
  },
  {
    id: 'shelfqr', preview: true,
    icon: '🗄️',
    titleKey: 'help.guides.shelfqr.title',
    descKey: 'help.guides.shelfqr.desc',
    steps: [
      { id: 1, icon: '🗄️', titleKey: 'help.guides.shelfqr.s1.title', textKey: 'help.guides.shelfqr.s1.text' },
      { id: 2, icon: '🖨️', img: 'regal-qr.jpg', titleKey: 'help.guides.shelfqr.s2.title', textKey: 'help.guides.shelfqr.s2.text' },
      { id: 3, icon: '🔛', titleKey: 'help.guides.shelfqr.s3.title', textKey: 'help.guides.shelfqr.s3.text' },
      { id: 4, icon: '📷', titleKey: 'help.guides.shelfqr.s4.title', textKey: 'help.guides.shelfqr.s4.text' },
    ]
  },
  {
    id: 'vinted', preview: true,
    icon: '📦',
    titleKey: 'help.guides.vinted.title',
    descKey: 'help.guides.vinted.desc',
    steps: [
      { id: 1, icon: '📦', titleKey: 'help.guides.vinted.s1.title', textKey: 'help.guides.vinted.s1.text' },
      { id: 2, icon: '🖼️', titleKey: 'help.guides.vinted.s2.title', textKey: 'help.guides.vinted.s2.text' },
      { id: 3, icon: '📱', titleKey: 'help.guides.vinted.s3.title', textKey: 'help.guides.vinted.s3.text' },
      { id: 4, icon: '🏷️', img: 'vinted-etikett.jpg', titleKey: 'help.guides.vinted.s4.title', textKey: 'help.guides.vinted.s4.text' },
      { id: 5, icon: '🔢', titleKey: 'help.guides.vinted.s5.title', textKey: 'help.guides.vinted.s5.text' },
    ]
  },
  {
    id: 'capture', preview: true,
    icon: '⚡',
    titleKey: 'help.guides.capture.title',
    descKey: 'help.guides.capture.desc',
    steps: [
      { id: 1, icon: '📷', img: 'setup-5.jpg', titleKey: 'help.guides.capture.s1.title', textKey: 'help.guides.capture.s1.text' },
      { id: 2, icon: '⚡', titleKey: 'help.guides.capture.s2.title', textKey: 'help.guides.capture.s2.text' },
      { id: 3, icon: '📋', video: 'batch', titleKey: 'help.guides.capture.s3.title', textKey: 'help.guides.capture.s3.text' },
      { id: 4, icon: '📥', titleKey: 'help.guides.capture.s4.title', textKey: 'help.guides.capture.s4.text' },
    ]
  },
  {
    id: 'batch', preview: true,
    icon: '📋',
    titleKey: 'help.guides.batch.title',
    descKey: 'help.guides.batch.desc',
    steps: [
      { id: 1, icon: '🎬', video: 'batch', titleKey: 'help.guides.batch.s1.title', textKey: 'help.guides.batch.s1.text' },
      { id: 2, icon: '🔍', titleKey: 'help.guides.batch.s2.title', textKey: 'help.guides.batch.s2.text' },
      { id: 3, icon: '📱', titleKey: 'help.guides.batch.s3.title', textKey: 'help.guides.batch.s3.text' },
      { id: 4, icon: '🎙️', titleKey: 'help.guides.batch.s4.title', textKey: 'help.guides.batch.s4.text' },
      { id: 5, icon: '⏸️', titleKey: 'help.guides.batch.s5.title', textKey: 'help.guides.batch.s5.text' },
      { id: 6, icon: '🕒', titleKey: 'help.guides.batch.s6.title', textKey: 'help.guides.batch.s6.text' },
    ]
  },
  {
    id: 'staff', preview: true,
    icon: '👷',
    titleKey: 'help.guides.staff.title',
    descKey: 'help.guides.staff.desc',
    steps: [
      { id: 1, icon: '👷', titleKey: 'help.guides.staff.s1.title', textKey: 'help.guides.staff.s1.text' },
      { id: 2, icon: '🔐', img: 'mitarbeiter.jpg', titleKey: 'help.guides.staff.s2.title', textKey: 'help.guides.staff.s2.text' },
      { id: 3, icon: '🔓', titleKey: 'help.guides.staff.s3.title', textKey: 'help.guides.staff.s3.text' },
    ]
  },
];

// Bilder gibt es je Sprache (public/help/<sprache>/). Fehlt eines, wird das englische gezeigt.

const whatsNewData = {
  '2.4.4': [
    { icon: '🧭', textKey: 'help.whatsnew.244.setup' },
    { icon: '🖨️', textKey: 'help.whatsnew.244.printer' },
    { icon: '🎨', textKey: 'help.whatsnew.244.label' },
    { icon: '🧩', textKey: 'help.whatsnew.244.options' },
    { icon: '🤖', textKey: 'help.whatsnew.244.ai' },
  ],
  '2.4.0': [
    { icon: '🧭', textKey: 'help.whatsnew.240.rebrand' },
    { icon: '🔴', textKey: 'help.whatsnew.240.livePreview' },
    { icon: '🎯', textKey: 'help.whatsnew.240.sharpPickup' },
    { icon: '🔎', textKey: 'help.whatsnew.240.lensOnly' },
    { icon: '🛡️', textKey: 'help.whatsnew.240.overwriteGuard' },
  ],
  '2.3.0': [
    { icon: '📸', textKey: 'help.whatsnew.230.cameraStation' },
    { icon: '🤖', textKey: 'help.whatsnew.230.aiCapture' },
    { icon: '🪪', textKey: 'help.whatsnew.230.idRectify' },
    { icon: '🖨️', textKey: 'help.whatsnew.230.printer' },
    { icon: '🔦', textKey: 'help.whatsnew.230.expertCam' },
    { icon: '📖', textKey: 'help.whatsnew.230.tutorial' },
  ],
  '1.41': [
    { icon: '💡', textKey: 'help.whatsnew.141.helpCenter' },
    { icon: '⭐', textKey: 'help.whatsnew.141.premium' },
    { icon: '🌙', textKey: 'help.whatsnew.141.darkMode' },
    { icon: '🌍', textKey: 'help.whatsnew.141.languages' },
    { icon: '💾', textKey: 'help.whatsnew.141.settings' },
  ],
  '1.40': [
    { icon: '📬', textKey: 'help.whatsnew.140.waitingList' },
    { icon: '🔔', textKey: 'help.whatsnew.140.notifications' },
    { icon: '📊', textKey: 'help.whatsnew.140.statistics' },
  ],
  '1.39': [
    { icon: '📱', textKey: 'help.whatsnew.139.tabletMode' },
    { icon: '🔍', textKey: 'help.whatsnew.139.customerSearch' },
  ],
  '1.38': [
    { icon: '☁️', textKey: 'help.whatsnew.138.cloudSync' },
    { icon: '🔄', textKey: 'help.whatsnew.138.multiDevice' },
  ],
  '1.37': [
    { icon: '📷', textKey: 'help.whatsnew.137.ocrImproved' },
    { icon: '🗄️', textKey: 'help.whatsnew.137.shelfManagement' },
  ],
  '1.36': [
    { icon: '🕶️', textKey: 'help.whatsnew.136.questSupport' },
    { icon: '🎯', textKey: 'help.whatsnew.136.vrPackages' },
  ],
};

const faqItems = [
  { id: 1, category: 'pricing', questionKey: 'help.faq.q1', answerKey: 'help.faq.a1' },
  { id: 2, category: 'pricing', questionKey: 'help.faq.q2', answerKey: 'help.faq.a2' },
  { id: 3, category: 'pricing', questionKey: 'help.faq.q3', answerKey: 'help.faq.a3' },
  { id: 4, category: 'pricing', questionKey: 'help.faq.q4', answerKey: 'help.faq.a4' },
  { id: 5, category: 'packages', questionKey: 'help.faq.q5', answerKey: 'help.faq.a5' },
  { id: 6, category: 'packages', questionKey: 'help.faq.q6', answerKey: 'help.faq.a6' },
  { id: 7, category: 'packages', questionKey: 'help.faq.q7', answerKey: 'help.faq.a7' },
  { id: 8, category: 'sync', questionKey: 'help.faq.q8', answerKey: 'help.faq.a8' },
  { id: 9, category: 'tablet', questionKey: 'help.faq.q9', answerKey: 'help.faq.a9' },
  { id: 10, category: 'settings', questionKey: 'help.faq.q10', answerKey: 'help.faq.a10' },
  // Fragen zu Setup, Drucker, Regal-QR, Absender — siehe preview oben
  { id: 11, preview: true, category: 'settings', questionKey: 'help.faq.q11', answerKey: 'help.faq.a11' },
  { id: 12, preview: true, category: 'settings', questionKey: 'help.faq.q12', answerKey: 'help.faq.a12' },
  { id: 13, preview: true, category: 'settings', questionKey: 'help.faq.q13', answerKey: 'help.faq.a13' },
  { id: 14, preview: true, category: 'packages', questionKey: 'help.faq.q14', answerKey: 'help.faq.a14' },
  { id: 15, preview: true, category: 'packages', questionKey: 'help.faq.q15', answerKey: 'help.faq.a15' },
  { id: 16, preview: true, category: 'sync', questionKey: 'help.faq.q16', answerKey: 'help.faq.a16' },
  { id: 17, preview: true, category: 'settings', questionKey: 'help.faq.q17', answerKey: 'help.faq.a17' },
  { id: 18, preview: true, category: 'settings', questionKey: 'help.faq.q18', answerKey: 'help.faq.a18' },
  { id: 19, preview: true, category: 'packages', questionKey: 'help.faq.q19', answerKey: 'help.faq.a19' },
  { id: 20, preview: true, category: 'settings', questionKey: 'help.faq.q20', answerKey: 'help.faq.a20' },
  { id: 21, preview: true, category: 'settings', questionKey: 'help.faq.q21', answerKey: 'help.faq.a21' },
  { id: 22, preview: true, category: 'settings', questionKey: 'help.faq.q22', answerKey: 'help.faq.a22' },
  { id: 23, preview: true, category: 'settings', questionKey: 'help.faq.q23', answerKey: 'help.faq.a23' },
];

// ============================================
// 🎓 TUTORIAL MENU COMPONENT
// ============================================

function TutorialMenu({ onSelectCategory, onBack, darkMode }) {
  const { t } = useTranslation();

  return (
    <div className="tutorial-menu">
      <button className="help-back-btn" onClick={onBack}>
        ← {t('common.back')}
      </button>

      <h3 className="tutorial-menu-title">{t('help.tutorials.selectTopic')}</h3>

      <div className="tutorial-categories">
        {tutorialCategories.filter(cat => !cat.preview || isSetupEnabled()).map(cat => (
          <button
            key={cat.id}
            data-testid={`help-cat-${cat.id}`}
            className="tutorial-category-item"
            onClick={() => onSelectCategory(cat)}
          >
            <span className="tutorial-category-icon">{cat.icon}</span>
            <div className="tutorial-category-info">
              <span className="tutorial-category-title">{t(cat.titleKey)}</span>
              <span className="tutorial-category-desc">{t(cat.descKey)}</span>
            </div>
            <span className="tutorial-category-arrow">→</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ============================================
// 🎓 TUTORIAL FLOW COMPONENT
// ============================================

function TutorialFlow({ category, onBack, onClose, darkMode }) {
  const { t, language } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const bildSprache = /^[a-z]{2}$/.test(language || '') ? language : 'en';

  const steps = category.steps;

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Tutorial fertig - bei Grundlagen speichern
      if (category.id === 'basics') {
        localStorage.setItem('tutorialCompleted', 'true');
      }
      onBack(); // Zurück zum Kategorie-Menü
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const step = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;

  return (
    <div className="tutorial-container">
      <button className="help-back-btn" onClick={onBack}>
        ← {t('common.back')}
      </button>

      <div className="tutorial-category-badge">
        {category.icon} {t(category.titleKey)}
      </div>

      <div className="tutorial-step">
        {!step.img && <div className="tutorial-icon">{step.icon}</div>}
        <h3 className="tutorial-title">{t(step.titleKey)}</h3>
        <p className="tutorial-text">{t(step.textKey)}</p>
        {step.video === 'batch' && (
          <div style={{ textAlign: 'left', marginTop: 12 }}>
            <BatchTutorialVideo compact />
          </div>
        )}
        {/* Bild aus der App: erst der Text, darunter das Bild in voller Breite (das Fenster scrollt) */}
        {step.img && (
          <img
            key={step.img}
            data-testid="help-img"
            src={`${process.env.PUBLIC_URL}/help/${bildSprache}/${step.img}`}
            alt={t(step.titleKey)}
            onError={(e) => {
              const el = e.currentTarget;
              const ersatz = `${process.env.PUBLIC_URL}/help/en/${step.img}`;
              if (bildSprache !== 'en' && !el.dataset.ersatz) { el.dataset.ersatz = '1'; el.src = ersatz; } else el.style.display = 'none';
            }}
            style={{ display: 'block', width: '100%', maxWidth: 420, margin: '12px auto 0', borderRadius: 10,
              border: `1px solid ${darkMode ? '#444' : '#ddd'}`, boxShadow: '0 2px 10px rgba(0,0,0,0.15)' }}
          />
        )}
      </div>

      <div className="tutorial-progress">
        {steps.map((_, idx) => (
          <div 
            key={idx} 
            className={`tutorial-dot ${idx === currentStep ? 'active' : ''}`}
          />
        ))}
      </div>

      <div className="tutorial-nav">
        <button 
          className="tutorial-nav-btn secondary"
          onClick={handlePrev}
          disabled={currentStep === 0}
          style={{ opacity: currentStep === 0 ? 0.5 : 1 }}
        >
          {t('common.back')}
        </button>
        <button className="tutorial-nav-btn primary" onClick={handleNext}>
          {isLastStep ? t('help.tutorial.finish') : t('common.next')}
        </button>
      </div>
    </div>
  );
}

// ============================================
// ✨ WHAT'S NEW COMPONENT
// ============================================

function WhatsNew({ onBack, darkMode }) {
  const { t } = useTranslation();
  const [showOlder, setShowOlder] = useState(false);
  
  const versions = Object.entries(whatsNewData);
  const latestVersion = versions[0];
  const olderVersions = versions.slice(1);

  return (
    <div>
      <button className="help-back-btn" onClick={onBack}>
        ← {t('common.back')}
      </button>

      {/* Aktuelle Version */}
      <div className="whats-new-version current">
        <h3 className="whats-new-version-title">
          ✨ Version {latestVersion[0]} <span className="version-badge">{t('help.whatsnew.current')}</span>
        </h3>
        <div className="whats-new-list">
          {latestVersion[1].map((item, idx) => (
            <div key={idx} className="whats-new-item">
              <span className="whats-new-icon">{item.icon}</span>
              <span>{t(item.textKey)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Ältere Versionen Toggle */}
      {olderVersions.length > 0 && (
        <>
          <button 
            className="older-versions-toggle"
            onClick={() => setShowOlder(!showOlder)}
          >
            <span>{showOlder ? '▼' : '▶'}</span>
            <span>{t('help.whatsnew.olderVersions')} ({olderVersions.length})</span>
          </button>

          {showOlder && (
            <div className="older-versions-list">
              {olderVersions.map(([version, items]) => (
                <div key={version} className="whats-new-version older">
                  <h4 className="whats-new-version-title">Version {version}</h4>
                  <div className="whats-new-list">
                    {items.map((item, idx) => (
                      <div key={idx} className="whats-new-item">
                        <span className="whats-new-icon">{item.icon}</span>
                        <span>{t(item.textKey)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ============================================
// ❓ FAQ COMPONENT
// ============================================

function FAQ({ onBack, darkMode }) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [openItems, setOpenItems] = useState(new Set());

  const toggleItem = (id) => {
    const newOpen = new Set(openItems);
    if (newOpen.has(id)) {
      newOpen.delete(id);
    } else {
      newOpen.add(id);
    }
    setOpenItems(newOpen);
  };

  const filteredFaq = faqItems.filter(item => {
    if (item.preview && !isSetupEnabled()) return false;
    if (!searchQuery) return true;
    const question = t(item.questionKey).toLowerCase();
    const answer = t(item.answerKey).toLowerCase();
    return question.includes(searchQuery.toLowerCase()) || 
           answer.includes(searchQuery.toLowerCase());
  });

  return (
    <div>
      <button className="help-back-btn" onClick={onBack}>
        ← {t('common.back')}
      </button>

      <div className="faq-search">
        <input
          type="text"
          className="faq-search-input"
          placeholder={t('help.faq.searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      <div className="faq-list">
        {filteredFaq.length > 0 ? (
          filteredFaq.map(item => (
            <div key={item.id} className="faq-item">
              <button 
                className="faq-question"
                onClick={() => toggleItem(item.id)}
              >
                <span>{t(item.questionKey)}</span>
                <span className={`faq-arrow ${openItems.has(item.id) ? 'open' : ''}`}>
                  ▼
                </span>
              </button>
              {openItems.has(item.id) && (
                <div className="faq-answer">
                  {t(item.answerKey)}
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="faq-empty">{t('help.faq.noResults')}</div>
        )}
      </div>
    </div>
  );
}

// ============================================
// 💬 CONTACT & FEEDBACK COMPONENT
// ============================================

function Contact({ onBack, darkMode }) {
  const { t, language } = useTranslation();
  const tr = makeTr(language);
  const [category, setCategory] = useState('💡 Wunsch / Idee');
  const [message, setMessage] = useState('');
  const [contactReply, setContactReply] = useState(() => {
    try {
      return (JSON.parse(localStorage.getItem('kiosk_user_data') || '{}') || {}).email || '';
    } catch { return ''; }
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSend = async (e) => {
    e.preventDefault();
    if (!message.trim()) {
      setError(tr('Bitte gib eine Nachricht oder dein Feedback ein.', 'Please enter a message or your feedback.', 'Voer een bericht of je feedback in.'));
      return;
    }
    setSending(true);
    setError('');
    try {
      await submitUserFeedback({ category, message, contactReply });
      setSent(true);
      setMessage('');
    } catch (err) {
      setError(err?.message || tr('Fehler beim Senden. Bitte versuche es erneut.', 'Error sending. Please try again.', 'Fout bij verzenden. Probeer het opnieuw.'));
    } finally {
      setSending(false);
    }
  };

  const categories = [
    { value: '💡 Wunsch / Idee', label: tr('💡 Wunsch / Idee', '💡 Feature Request / Idea', '💡 Wens / Idee') },
    { value: '🐛 Problem melden', label: tr('🐛 Problem melden', '🐛 Report Issue', '🐛 Probleem melden') },
    { value: '❓ Frage / Kontakt', label: tr('❓ Frage / Kontakt', '❓ Question / Contact', '❓ Vraag / Contact') },
  ];

  return (
    <div className="contact-section" style={{ textAlign: 'left' }}>
      <button className="help-back-btn" onClick={onBack}>
        ← {t('common.back')}
      </button>

      <div style={{ textAlign: 'center', marginBottom: 14 }}>
        <div className="contact-icon" style={{ fontSize: 36, marginBottom: 4 }}>💬</div>
        <h3 style={{ margin: '0 0 4px 0', fontSize: 17, color: darkMode ? '#fff' : '#1e293b' }}>
          {tr('Feedback, Wünsche & direkter Kontakt', 'Feedback, Feature Requests & Direct Contact', 'Feedback, Wensen & Direct Contact')}
        </h3>
        <p className="contact-text" style={{ margin: 0, fontSize: 13 }}>
          {tr(
            'Schreib uns direkt hier aus der App — deine Nachricht landet sofort bei uns per E-Mail.',
            'Write to us directly from the app — your message reaches our team immediately by email.',
            'Stuur ons direct vanuit de app een bericht — je bericht komt direct bij ons binnen per e-mail.'
          )}
        </p>
      </div>

      {sent ? (
        <div style={{
          padding: 18, borderRadius: 12, textAlign: 'center',
          background: darkMode ? 'rgba(72,187,120,0.16)' : '#f0fff4',
          border: '1.5px solid #48bb78', color: darkMode ? '#9ae6b4' : '#22543d', marginBottom: 14
        }}>
          <div style={{ fontSize: 32, marginBottom: 6 }}>✅</div>
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>
            {tr('Vielen Dank! Deine Nachricht wurde gesendet.', 'Thank you! Your message has been sent.', 'Bedankt! Je bericht is verzonden.')}
          </div>
          <div style={{ fontSize: 12, opacity: 0.9, marginBottom: 12 }}>
            {tr(
              'Wir haben dein Feedback erhalten und melden uns zeitnah bei dir.',
              'We have received your feedback and will get back to you shortly.',
              'We hebben je feedback ontvangen en nemen snel contact met je op.'
            )}
          </div>
          <button
            type="button"
            onClick={() => setSent(false)}
            style={{
              padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer',
              background: '#48bb78', color: '#fff', fontWeight: 700, fontSize: 13
            }}
          >
            {tr('Weitere Nachricht schreiben', 'Send another message', 'Nog een bericht sturen')}
          </button>
        </div>
      ) : (
        <form onSubmit={handleSend} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {categories.map((cat) => {
              const active = category === cat.value;
              return (
                <button
                  key={cat.value}
                  type="button"
                  onClick={() => setCategory(cat.value)}
                  style={{
                    flex: 1, minWidth: 110, padding: '8px 10px', borderRadius: 8, fontSize: 12, fontWeight: 700,
                    cursor: 'pointer',
                    border: `1.5px solid ${active ? '#3182ce' : (darkMode ? '#4a5568' : '#cbd5e0')}`,
                    background: active ? (darkMode ? '#1e3a5f' : '#ebf8ff') : 'transparent',
                    color: active ? (darkMode ? '#90cdf4' : '#2b6cb0') : (darkMode ? '#cbd5e0' : '#4a5568'),
                  }}
                >
                  {cat.label}
                </button>
              );
            })}
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 700, marginBottom: 4, color: darkMode ? '#cbd5e0' : '#475569' }}>
              {tr('Deine Nachricht / Feedback *', 'Your Message / Feedback *', 'Jouw bericht / feedback *')}
            </label>
            <textarea
              rows={4}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={tr(
                'Was können wir verbessern, welche Funktion wünschst du dir oder wobei brauchst du Hilfe?',
                'What can we improve, what feature would you like, or how can we help you?',
                'Wat kunnen we verbeteren, welke functie wens je of waarbij heb je hulp nodig?'
              )}
              style={{
                width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 8, fontSize: 14,
                border: `1px solid ${darkMode ? '#4a5568' : '#cbd5e0'}`,
                background: darkMode ? '#1a202c' : '#fff',
                color: darkMode ? '#fff' : '#1e293b', resize: 'vertical', fontFamily: 'inherit'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 700, marginBottom: 4, color: darkMode ? '#cbd5e0' : '#475569' }}>
              {tr('E-Mail oder Telefonnummer für Rückfragen (optional)', 'Email or phone number for reply (optional)', 'E-mail of telefoonnummer voor reactie (optioneel)')}
            </label>
            <input
              type="text"
              value={contactReply}
              onChange={(e) => setContactReply(e.target.value)}
              placeholder={tr('z. B. name@shop.de oder 0176 ...', 'e.g. name@company.com or +1 ...', 'bijv. naam@winkel.nl of 06 ...')}
              style={{
                width: '100%', boxSizing: 'border-box', padding: '9px 12px', borderRadius: 8, fontSize: 13,
                border: `1px solid ${darkMode ? '#4a5568' : '#cbd5e0'}`,
                background: darkMode ? '#1a202c' : '#fff',
                color: darkMode ? '#fff' : '#1e293b'
              }}
            />
          </div>

          {error && (
            <div style={{ color: '#e53e3e', fontSize: 12, fontWeight: 600 }}>⚠️ {error}</div>
          )}

          <button
            type="submit"
            disabled={sending}
            data-testid="feedback-submit-btn"
            style={{
              padding: '12px 16px', borderRadius: 10, border: 'none', cursor: sending ? 'wait' : 'pointer',
              background: 'linear-gradient(135deg, #3182ce, #2b6cb0)', color: '#fff',
              fontWeight: 700, fontSize: 15, boxShadow: '0 2px 8px rgba(49,130,206,0.3)'
            }}
          >
            {sending
              ? tr('⏳ Wird gesendet…', '⏳ Sending…', '⏳ Verzenden…')
              : tr('📨 Feedback direkt absenden', '📨 Send Feedback Now', '📨 Feedback direct versturen')}
          </button>
        </form>
      )}

      <div style={{
        marginTop: 14, paddingTop: 12, borderTop: `1px solid ${darkMode ? '#334155' : '#e2e8f0'}`,
        textAlign: 'center', fontSize: 12, color: darkMode ? '#94a3b8' : '#64748b'
      }}>
        {tr('Oder direkt per E-Mail schreiben:', 'Or email us directly:', 'Of mail ons direct:')}{' '}
        <a
          href={`mailto:info@bobolution.de?subject=${language === 'de' ? 'PaketPilot' : 'ParcelPilot'}%20Feedback`}
          style={{ color: '#3182ce', fontWeight: 700, textDecoration: 'none' }}
        >
          ✉️ info@bobolution.de
        </a>
      </div>
    </div>
  );
}

// ============================================
// 💡 MAIN HELPCENTER COMPONENT
// ============================================

export default function HelpCenter({ isOpen, onClose, darkMode }) {
  const { t, language } = useTranslation();
  const tr = makeTr(language);
  const [view, setView] = useState('menu'); // menu, tutorials, tutorial-flow, whatsnew, faq, contact
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [hasSeenLatest, setHasSeenLatest] = useState(true);

  useEffect(() => {
    // Prüfen ob User die neueste Version gesehen hat
    const lastSeenVersion = localStorage.getItem('lastSeenHelpVersion');
    const currentVersion = Object.keys(whatsNewData)[0]; // Neueste Version
    if (lastSeenVersion !== currentVersion) {
      setHasSeenLatest(false);
    }
  }, []);

  const handleWhatsNewClick = () => {
    const currentVersion = Object.keys(whatsNewData)[0];
    localStorage.setItem('lastSeenHelpVersion', currentVersion);
    setHasSeenLatest(true);
    setView('whatsnew');
  };

  const handleSelectCategory = (category) => {
    setSelectedCategory(category);
    setView('tutorial-flow');
  };

  const handleClose = () => {
    setView('menu');
    setSelectedCategory(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="help-center-overlay" onClick={handleClose}>
      <div 
        className={`help-center-modal ${darkMode ? 'dark' : ''}`}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="help-center-header">
          <h2 className="help-center-title">
            💡 {t('help.title')}
          </h2>
          <button className="help-center-close" onClick={handleClose}>
            ×
          </button>
        </div>

        {/* Content */}
        <div className="help-center-content">
          {view === 'menu' && (
            <>
              <div className="help-menu-grid">
                <button className="help-menu-item" onClick={() => setView('tutorials')}>
                  <span className="help-menu-icon">🎓</span>
                  <span className="help-menu-label">{t('help.menu.tutorial')}</span>
                </button>
                
                <button className="help-menu-item" onClick={handleWhatsNewClick}>
                  <span className="help-menu-icon">✨</span>
                  <span className="help-menu-label">
                    {t('help.menu.whatsnew')}
                    {!hasSeenLatest && <span className="help-menu-badge">{t('setup.newBadge')}</span>}
                  </span>
                </button>
                
                <button className="help-menu-item" onClick={() => setView('faq')}>
                  <span className="help-menu-icon">❓</span>
                  <span className="help-menu-label">{t('help.menu.faq')}</span>
                </button>
                
                <button className="help-menu-item" data-testid="help-contact-tile" onClick={() => setView('contact')}>
                  <span className="help-menu-icon">💬</span>
                  <span className="help-menu-label">{t('help.menu.contact') !== 'help.menu.contact' ? t('help.menu.contact') : tr('Feedback & Kontakt', 'Feedback & Contact', 'Feedback & Contact')}</span>
                </button>
              </div>

              <button
                type="button"
                onClick={() => setView('contact')}
                style={{
                  width: '100%', marginTop: 12, padding: '12px 14px', borderRadius: 10,
                  border: `1.5px solid ${darkMode ? '#3b82f6' : '#93c5fd'}`,
                  background: darkMode ? 'rgba(59,130,246,0.14)' : '#eff6ff',
                  color: darkMode ? '#93c5fd' : '#1d4ed8',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  cursor: 'pointer', fontWeight: 700, fontSize: 13
                }}
              >
                <span>💬 {tr('Wunsch, Idee oder Problem direkt an uns senden', 'Send a feature request, idea or issue directly to us', 'Stuur een wens, idee of probleem direct naar ons')}</span>
                <span>→</span>
              </button>
            </>
          )}

          {view === 'tutorials' && (
            <TutorialMenu 
              onSelectCategory={handleSelectCategory}
              onBack={() => setView('menu')} 
              darkMode={darkMode}
            />
          )}

          {view === 'tutorial-flow' && selectedCategory && (
            <TutorialFlow 
              category={selectedCategory}
              onBack={() => setView('tutorials')} 
              onClose={handleClose}
              darkMode={darkMode}
            />
          )}

          {view === 'whatsnew' && (
            <WhatsNew 
              onBack={() => setView('menu')} 
              darkMode={darkMode}
            />
          )}

          {view === 'faq' && (
            <FAQ 
              onBack={() => setView('menu')} 
              darkMode={darkMode}
            />
          )}

          {view === 'contact' && (
            <Contact 
              onBack={() => setView('menu')} 
              darkMode={darkMode}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// Export für Header-Button
export function HelpTriggerButton({ onClick, darkMode }) {
  const { t } = useTranslation();
  return (
    <button 
      className="help-trigger-btn"
      onClick={onClick}
      title={t('help.title')}
      style={{ color: darkMode ? '#aaa' : '#666' }}
    >
      💡
    </button>
  );
}
